import { sanityClient } from "@/sanity/lib/client";
import { urlFor } from '@/sanity/lib/image';
import { PortableText } from "@portabletext/react";
import styles from "./blog.module.scss";
import { NextSeo } from "next-seo";
import Image from "next/image";
import Breadcrumb from "@/components/breadcrumb";
import { getSiteSettings } from "@/lib/getSiteSettings";
import ChevronRight from "/packages/icons/chevron-right.svg";
import ChevronLeft from "/packages/icons/chevron-left.svg";
import { portableTextComponents } from "@/lib/portableTextComponents";

export const getStaticPaths = async () => {
  const query = `*[_type == "post"]{
    _id,
    slug {
      current
    }
  }`;

  const posts = await sanityClient.fetch(query);

  const paths = posts.map((post) => ({
    params: {
      slug: post.slug.current,
    },
  }));

  return {
    paths,
    fallback: "blocking",
  };
};

export const getStaticProps = async ({ params }) => {
  const siteSettings = await getSiteSettings();

  const postQuery = `*[_type == "post" && slug.current == $slug][0]{
    _id,
    title,
    slug,
    publishedAt,
    metaTitle,
    metaDescription,
    body,
    mainImage {
      asset->{
        url,
        metadata { dimensions { width, height } }
      },
      alt
    }
  }`;

  const post = await sanityClient.fetch(postQuery, { slug: params.slug });
  if (!post) return { notFound: true };

  // Next post (older)
  const nextPostQuery = `*[_type == "post" && publishedAt < $publishedAt] 
    | order(publishedAt desc)[0]{
      title,
      slug,
      mainImage {
        asset->{
          url,
          metadata { dimensions { width, height } }
        },
        alt
      }
    }`;

  // Previous post (newer)
  const previousPostQuery = `*[_type == "post" && publishedAt > $publishedAt] 
    | order(publishedAt asc)[0]{
      title,
      slug,
      mainImage {
        asset->{
          url,
          metadata { dimensions { width, height } }
        },
        alt
      }
    }`;

  const [nextPost, previousPost] = await Promise.all([
    sanityClient.fetch(nextPostQuery, { publishedAt: post.publishedAt }),
    sanityClient.fetch(previousPostQuery, { publishedAt: post.publishedAt }),
  ]);

  return {
    props: {
      siteSettings,
      post,
      previousPost,
      nextPost,
    },
    revalidate: 60,
  };
};


const truncate = (str, max = 160) => {
  if (!str) return '';
  return str.length > max ? str.slice(0, max).trimEnd() + '…' : str;
};

const flattenPortableText = (blocks) => {
  return blocks
    ?.map((block) => (block._type === 'block' ? block.children?.map(child => child.text).join('') : ''))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const truncateNav = (str, max = 70) =>
  str.length > max ? str.slice(0, max) + '…' : str;


export default function Post({ post, previousPost, nextPost }) {
  const ogImageUrl = urlFor(post.mainImage)
  .width(1200)
  .height(630)
  .fit('crop')
  .url();

  return (
    <div className={styles.container}>
      <NextSeo
        title={truncate(post.metaTitle || post.title, 60)}
        description={truncate(post.metaDescription || flattenPortableText(post.body), 160)}
        canonical={`https://plpcsanjose.com/blog/${post.slug.current}`}
        openGraph={{
          type: 'article',
          url: `https://plpcsanjose.com/blog/${post.slug.current}`,
          title: truncate(post.metaTitle || post.title, 60),
          description: truncate(post.metaDescription || flattenPortableText(post.body), 160),
          images: [
            {
              url: ogImageUrl,
              width: 1200,
              height: 630,
              alt: post.mainImage.alt || post.title,
            },
          ],
          article: {
            publishedTime: post.publishedAt,
          },
          site_name: 'Purple Lotus',
        }}
        twitter={{
          cardType: 'summary_large_image',
        }}
      />
      <div className={styles.post}>
        <div className={styles.breadcrumb}>
          <Breadcrumb />
        </div>
        <div className={styles.featuredImage}>
          <Image
            src={post.mainImage.asset.url}
            alt={post.mainImage.alt || post.title}
            width={post.mainImage.asset.metadata.dimensions.width}
            height={post.mainImage.asset.metadata.dimensions.height}
            layout="intrinsic"
            style={{ objectFit: "contain" }}
          />
        </div>

        <article>
          <div className={styles.date}>
            {new Date(post.publishedAt).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </div>
          <h1 className={styles.title}>{post.title}</h1>
          <PortableText value={post.body} components={portableTextComponents} />
        </article>

        <nav className={styles.postNavigation}>
          {previousPost && (
            <a
              href={`/blog/${previousPost.slug.current}`}
              className={`${styles.navItem} ${styles.prev}`}
            >
              <span className={styles.navLabel}>
                <ChevronLeft /> Prev Post
              </span>
              <div className={styles.imageWrapper}>
                <img
                  src={previousPost.mainImage.asset.url}
                  alt={previousPost.mainImage.alt || previousPost.title}
                />
              </div>
              <h3>{truncateNav(previousPost.title)}</h3>
            </a>
          )}
          {nextPost && (
            <a
              href={`/blog/${nextPost.slug.current}`}
              className={`${styles.navItem} ${styles.next}`}
            >
              <span className={styles.navLabel}>
                Next Post <ChevronRight />
              </span>
              <div className={styles.imageWrapper}>
                <img
                  src={nextPost.mainImage.asset.url}
                  alt={nextPost.mainImage.alt || nextPost.title}
                />
              </div>
              <h3>{truncateNav(nextPost.title)}</h3>
            </a>
          )}
        </nav>
      </div>
    </div>
  );
}
